import { useState } from 'react';
import { useParams } from 'wouter';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer1';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { MapPin, Clock, Star, Award, Phone, Mail, MessageCircle, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { getMediaOwner } from '@/data/media-owners';

export default function ContactMediaOwner() {
  const params = useParams();
  const ownerId = params.ownerId;
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    inquiryType: 'general'
  });

  // Get media owner data or use default
  const mediaOwnerData = getMediaOwner(ownerId || '');
  const mediaOwner = mediaOwnerData ? {
    ...mediaOwnerData,
    phone: "+61 2 9876 5432",
    email: `contact@${mediaOwnerData.name.toLowerCase().replace(/[^a-z0-9]/g, '')}.com.au`
  } : {
    id: "ooh-unmissable",
    name: "OOH Unmissable Shop",
    logo: "/api/placeholder/120/120",
    tagline: "Premium Digital & Classic Billboard Solutions",
    rating: 4.9,
    reviewCount: 1984,
    responseTime: "Usually responds within 24 hours",
    location: "Sydney, Australia",
    phone: "+61 2 9876 5432",
    email: "contact@oohunmissable.com.au",
    verified: true
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Simulate form submission
    toast({
      title: "Message Sent Successfully",
      description: `Your inquiry has been sent to ${mediaOwner.name}. They typically respond within 24 hours.`,
    });
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      subject: '',
      message: '',
      inquiryType: 'general'
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation mode="header" />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-emerald-600 mb-4">
            Contact Media Owner
          </h1>
          <p className="text-lg text-gray-600 break-words px-4">
            Get in touch with {mediaOwner.name} for inquiries about their advertising solutions
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Media Owner Info */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="relative flex-shrink-0">
                    <img
                      src={mediaOwner.logo}
                      alt={mediaOwner.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    {mediaOwner.verified && (
                      <div className="absolute -bottom-1 -right-1 bg-emerald-500 rounded-full p-1">
                        <Award className="h-3 w-3 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg break-words">{mediaOwner.name}</CardTitle>
                    <p className="text-sm text-gray-600 break-words">{mediaOwner.tagline}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Star className="h-4 w-4 text-yellow-400 fill-current flex-shrink-0" />
                  <span className="font-medium">{mediaOwner.rating}</span>
                  <span className="text-gray-600 truncate">({mediaOwner.reviewCount} reviews)</span>
                </div>
                
                <div className="flex items-start space-x-2 text-gray-600">
                  <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span className="text-sm break-words">{mediaOwner.location}</span>
                </div>
                
                <div className="flex items-start space-x-2 text-gray-600">
                  <Clock className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span className="text-sm break-words">{mediaOwner.responseTime}</span>
                </div>
                
                <div className="pt-4 border-t space-y-3">
                  <div className="flex items-start space-x-2">
                    <Phone className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm break-all">{mediaOwner.phone}</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Mail className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm break-all">{mediaOwner.email}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MessageCircle className="h-5 w-5 text-emerald-600" />
                  <span>Send Message</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company">Company Name</Label>
                      <Input
                        id="company"
                        type="text"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="inquiryType">Inquiry Type</Label>
                    <Select value={formData.inquiryType} onValueChange={(value) => handleInputChange('inquiryType', value)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General Inquiry</SelectItem>
                        <SelectItem value="pricing">Pricing Information</SelectItem>
                        <SelectItem value="availability">Availability Check</SelectItem>
                        <SelectItem value="partnership">Partnership Opportunity</SelectItem>
                        <SelectItem value="support">Technical Support</SelectItem>
                        <SelectItem value="bulk">Bulk Advertising</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      type="text"
                      value={formData.subject}
                      onChange={(e) => handleInputChange('subject', e.target.value)}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      required
                      rows={6}
                      className="mt-1 resize-none"
                      placeholder="Please provide details about your advertising needs, preferred locations, budget range, and timeline..."
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    size="lg"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer configData={[]} yourLogo={""} />
    </div>
  );
}